import * as React from 'react';
import { observer } from 'mobx-react';
import Page from '@src/components/page';
import BottomNav from '@src/business-components/bottom-nav';
import $$tracker from '@src/tracker';
import useStore from '@src/utils/hooks/use-store';
import MainStore from './store';

const styles = require('./index.less');
const pageBackground = require('./images/page-background-sm.jpg');
const reserveSuccess = require('./images/reserve-success.png');
const guestPage = require('./images/team-guest.png');
const guestButtonActive = require('./images/team-button-active.png');
const guestButtonJoin = require('./images/join-team-btn.png');
const guestButtonFull = require('./images/team-full-btn.png');
const guestButtonCreate = require('./images/team-button-create.png');
const guestButtonReserve = require('./images/team-button-reserve.png');

const getHotspotStyle = (left, top, width, height) => ({
  left: `${left / 7.5}%`,
  top: `${(top / 1432) * 100}%`,
  width: `${width / 7.5}%`,
  height: `${(height / 1432) * 100}%`
});

/**
 * 登录态文案：未登录显示"你好，请【登录】"（点击拉起登录弹窗），
 * 已登录显示"账号，【退出】"（点击退出登录）。账号来自 XPASS，SDK 侧已做脱敏。
 */
const renderAccountStatus = (store, className) => (
  <div className={className}>
    {store.displayAccount ? (
      <React.Fragment>
        <span>{store.displayAccount}，</span>
        <button className={styles.statusActionButton} type="button" onClick={() => store.logout()}>
          【退出】
        </button>
      </React.Fragment>
    ) : (
      <React.Fragment>
        <span>你好，请</span>
        <button className={styles.statusActionButton} type="button" onClick={() => store.login()}>
          【登录】
        </button>
      </React.Fragment>
    )}
  </div>
);

const Main = () => {
  const store = useStore(() => new MainStore());
  const guestViewModel = store.teamGuestViewModel;
  const guestPrimaryButtonArtwork =
    guestViewModel.actionState === 'team_full' ? guestButtonFull : guestViewModel.primaryAction === 'join' ? guestButtonJoin : guestButtonActive;
  const showGuestPrimaryButtonLabel = guestViewModel.actionState !== 'team_full' && guestViewModel.primaryAction !== 'join';
  const reservedHeroesRef = React.useRef(null);
  const reservedHeroesGridRef = React.useRef(null);
  const [shouldScrollHeroes, setShouldScrollHeroes] = React.useState(false);

  React.useEffect(
    () => {
      const updateScrollState = () => {
        const container = reservedHeroesRef.current;
        const grid = reservedHeroesGridRef.current;
        setShouldScrollHeroes(Boolean(container && grid && grid.scrollHeight > container.clientHeight));
      };

      updateScrollState();
      window.addEventListener('resize', updateScrollState);
      return () => window.removeEventListener('resize', updateScrollState);
    },
    [store.isTeamGuestMode, store.reservationHeroes.length]
  );

  return (
    <Page className={store.isTeamGuestMode ? styles.guestRoot : styles.root}>
      <main className={styles.page} aria-label="剑侠世界端游新服预约专题">
        {store.isTeamGuestMode ? (
          <img className={styles.guestPage} src={guestPage} alt="钜惠组队礼包邀请页" />
        ) : (
          <section className={styles.campaign} style={{ backgroundImage: `url(${pageBackground})` }} aria-label="新服预约活动">
            <span className={`${styles.serverName} ${styles.heroServerName}`}>【{store.serverName}】</span>
            <span className={styles.reservationCount}>当前预约人数：{store.reservationCount}位</span>
            {/* 非客态登录文案：沿用原账号区域的位置。 */}
            {renderAccountStatus(store, styles.accountStatus)}
            <button className={`${styles.hotspot} ${styles.rulesButton}`} type="button" onClick={() => store.openActivityRules()} aria-label="活动规则" />
            <div className={styles.reservedHeroes} ref={reservedHeroesRef} aria-label="已预约大侠名单">
              {store.reservationHeroes.length > 0 ? (
                <div
                  className={`${styles.reservedHeroesTrack} ${shouldScrollHeroes ? styles.reservedHeroesTrackScrolling : ''}`}
                  style={shouldScrollHeroes ? { animationDuration: `${Math.max(16, store.reservationHeroes.length * 2.4)}s` } : undefined}
                >
                  {(shouldScrollHeroes ? [0, 1] : [0]).map(copyIndex => (
                    <div
                      className={styles.reservedHeroesGrid}
                      ref={copyIndex === 0 ? reservedHeroesGridRef : null}
                      key={copyIndex}
                      aria-hidden={copyIndex === 1}
                    >
                      {store.reservationHeroes.map(item => (
                        <div className={styles.reservedHeroItem} key={`${item.id}-${copyIndex}`} title={item.displayText}>
                          {item.displayText}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.reservedHeroesEmpty}>暂无数据</div>
              )}
            </div>
            <img className={`${styles.reserveSuccessArtwork} ${store.isReserved ? styles.isVisible : styles.isHidden}`} src={reserveSuccess} alt="" />
            <button
              id="reservation-action"
              className={`${styles.hotspot} ${styles.reserveButton}`}
              type="button"
              disabled={store.isReserved}
              onClick={() => store.handleReserveClick()}
              aria-label={store.isReserved ? '已预约' : `预约送绑金${store.bindGoldText}`}
            />
            <a
              className={styles.hotspot}
              style={getHotspotStyle(0, 4, 193, 90)}
              href="https://jxsj.xoyo.com/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => $$tracker.clickGoGuanwang()}
              aria-label="剑侠世界端游官网"
            >
              <span className={styles.visuallyHidden}>剑侠世界端游官网</span>
            </a>
            <a
              className={styles.hotspot}
              style={getHotspotStyle(410, 32, 154, 41)}
              href="https://jxsj.xoyo.com/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => store.onOfficialSiteClick()}
              aria-label="进入官网"
            >
              <span className={styles.visuallyHidden}>进入官网</span>
            </a>
            <button
              className={styles.hotspot}
              style={getHotspotStyle(577, 32, 154, 41)}
              type="button"
              onClick={() => store.onFeatureTopicClick()}
              aria-label="资料片专题"
            />
          </section>
        )}
        {store.isTeamGuestMode && (
          <React.Fragment>
            <a
              className={`${styles.hotspot} ${styles.guestLogo}`}
              href="https://jxsj.xoyo.com/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => $$tracker.clickGoGuanwang()}
              aria-label="剑侠世界端游官网"
            >
              <span className={styles.visuallyHidden}>剑侠世界端游官网</span>
            </a>
            <a
              className={`${styles.hotspot} ${styles.guestOfficialButton}`}
              href="https://jxsj.xoyo.com/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => store.onOfficialSiteClick()}
              aria-label="进入官网"
            >
              <span className={styles.visuallyHidden}>进入官网</span>
            </a>
            <button
              className={`${styles.hotspot} ${styles.guestFeatureButton}`}
              type="button"
              onClick={() => store.onFeatureTopicClick()}
              aria-label="资料片专题"
            />
            {/* 客态页登录文案独立定位，距页面顶部 410px。 */}
            {renderAccountStatus(store, `${styles.accountStatus} ${styles.guestAccountStatus}`)}
            <span className={`${styles.serverName} ${styles.guestHeroServerName}`}>【{store.serverName}】</span>
            {/*<span className={styles.guestActivityTime}>活动时间：10月8日-11月16日</span>*/}
            <div className={styles.guestInvitationCopy}>
              <span>
                我已预约《剑侠世界》首个生态服<span className={styles.guestInvitationServer}>【{store.serverName}】</span>
              </span>
              <span>快和我组队购买钜惠礼包，一起玩新服吧！</span>
            </div>
            {guestViewModel.showPrimary && (
              <button
                className={`${styles.hotspot} ${styles.guestJoinButton} ${styles.guestArtworkButton} ${
                  !guestViewModel.canPrimaryAction && guestViewModel.actionState !== 'team_full' ? styles.guestDisabledButton : ''
                }`}
                type="button"
                disabled={!guestViewModel.canPrimaryAction}
                onClick={() => store.handleGuestPrimaryAction()}
                aria-label={guestViewModel.primaryLabel}
              >
                <img className={styles.guestButtonArtwork} src={guestPrimaryButtonArtwork} alt="" />
                {showGuestPrimaryButtonLabel && <span className={styles.guestButtonLabel}>{guestViewModel.primaryLabel}</span>}
              </button>
            )}
            {guestViewModel.showCreate && (
              <button
                className={`${styles.hotspot} ${styles.guestCreateButton} ${styles.guestArtworkButton}`}
                type="button"
                disabled={!guestViewModel.canCreate}
                onClick={() => store.handleGuestCreate()}
                aria-label="创建购买队伍"
              >
                <img className={styles.guestButtonArtwork} src={guestButtonCreate} alt="" />
              </button>
            )}
            <button
              className={`${styles.hotspot} ${styles.guestReserveButton} ${styles.guestArtworkButton} ${
                !guestViewModel.showCreate ? styles.guestCondensedReserveButton : ''
              }`}
              type="button"
              onClick={() => store.handleGuestReserveAction()}
              aria-label={guestViewModel.reserveLabel}
            >
              <img className={styles.guestButtonArtwork} src={guestButtonReserve} alt="" />
            </button>
          </React.Fragment>
        )}
      </main>
      {!store.isTeamGuestMode && <BottomNav active="MAIN" />}
    </Page>
  );
};

export default observer(Main);
